<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { LazyTooltip } from '@/components/ui/tooltip';
import { buildEntityColumns, entityDisplayNameFor } from '@/composables/entityColumns';
import { useRecordLayout } from '@/composables/recordLayout/useRecordLayout';
import { areaEntity, nounInSentence } from '@/navigation/areas';
import CombinedRecordBody from '@/components/record/CombinedRecordBody.vue';
import CustomizeRecordDialog from '@/components/record/CustomizeRecordDialog.vue';
import RecordMoreMenu from '@/components/record/RecordMoreMenu.vue';

/**
 * The right pane when two or more records are selected: one combined record,
 * drawn in the person's own layout, showing which values the records share.
 * Spec: docs/specs/sau-600-combined-record.md.
 *
 * The heading names the count and the area's plural noun, such as "3 zones".
 * The ⋯ menu holds only "Customize page…", which edits the same shared layout
 * one record uses, so a change shows here at once. There is no full-page button.
 *
 * When the selection covers records the page has not loaded, such as after
 * "Select all 112 matching", the pane shows the count and one line instead of
 * values worked out from part of the selection.
 */
const props = defineProps<{
    /** The list's record type, such as "LocationZone". */
    entityId: string;
    /** The selected rows the list has loaded. */
    rows: Record<string, unknown>[];
    /** How many records are selected, loaded or not. */
    count: number;
}>();

const emit = defineEmits<{
    /** Close the pane and clear the selection. */
    close: [];
}>();

const columns = computed(() => buildEntityColumns(props.entityId));
const headingNoun = computed(() => areaEntity(props.entityId)?.noun ?? entityDisplayNameFor(props.entityId));
const noun = computed(() => nounInSentence(headingNoun.value));
// Every area noun today takes a plain "s".
const heading = computed(() => `${props.count.toLocaleString()} ${noun.value}s`);
const allRowsLoaded = computed(() => props.rows.length === props.count);

const layout = useRecordLayout(() => props.entityId, columns);
const customizeOpen = ref(false);
const moreMenu = useTemplateRef<InstanceType<typeof RecordMoreMenu>>('moreMenu');

const iconButtonClass =
    'inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground';
</script>

<template>
  <div class="flex flex-col gap-4" data-testid="combined-record-pane">
    <div class="flex items-start justify-between gap-3">
      <h2 class="min-w-0 text-xl leading-tight font-semibold" data-testid="combined-record-heading">{{ heading }}</h2>
      <div class="flex shrink-0 items-center gap-1">
        <RecordMoreMenu
          ref="moreMenu"
          :entity-id="entityId"
          :record-id="null"
          :noun="headingNoun"
          can-customize
          size="compact"
          test-id-prefix="combined-record-more"
          @customize="customizeOpen = true"
        />
        <LazyTooltip side="bottom" align="end">
          <button
            type="button"
            data-testid="combined-record-close"
            aria-label="Close"
            :class="iconButtonClass"
            @click="emit('close')"
          >
            <XMarkIcon class="size-4" aria-hidden="true" />
          </button>
          <template #content>Close</template>
        </LazyTooltip>
      </div>
    </div>
    <CustomizeRecordDialog
      v-model:open="customizeOpen"
      :entity-id="entityId"
      :noun="noun"
      :columns="columns"
      :layout-state="layout"
      :is-developer="false"
      @closed="moreMenu?.focusTrigger()"
    />
    <CombinedRecordBody v-if="allRowsLoaded" :entity-id="entityId" :rows="rows" :layout="layout.shown.value" />
    <p v-else class="text-sm text-muted-foreground" data-testid="combined-record-not-loaded">Shared values can only be shown for records on the loaded page.</p>
  </div>
</template>
